/**
 * User Filters Panel Component
 * Provides filtering options: email search, role, status
 *
 * @module Features/UserManagement/Components
 */

import React from 'react';
import {
  Paper,
  TextField,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
  Button,
  Grid,
  Typography,
} from '@mui/material';
import { Clear as ClearIcon } from '@mui/icons-material';
import { UserRole } from '../Types';

interface UserFiltersPanelProps {
  filters: {
    searchEmail: string;
    role: string | null;
    isActive: boolean | null;
    emailConfirmed: boolean | null;
  };

  onSearchEmailChange: (email: string) => void;
  onRoleChange: (role: string | null) => void;
  onIsActiveChange: (value: boolean | null) => void;
  onEmailConfirmedChange: (value: boolean | null) => void;
  onClearFilters: () => void;
}

/**
 * User Filters Panel Component
 */
export const UserFiltersPanel: React.FC<UserFiltersPanelProps> = ({
  filters,
  onSearchEmailChange,
  onRoleChange,
  onIsActiveChange,
  onEmailConfirmedChange,
  onClearFilters,
}) => {
  const hasActiveFilters =
    filters.searchEmail ||
    filters.role ||
    filters.isActive !== null ||
    filters.emailConfirmed !== null;

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Filtry użytkowników
      </Typography>

      <Grid container spacing={2}>
        {/* Email search */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }} >
          <TextField
            fullWidth
            label="Szukaj według email"
            size="small"
            value={filters.searchEmail}
            onChange={(e) => onSearchEmailChange(e.target.value)}
            placeholder="np. john@agency.com"
            variant="outlined"
          />
        </Grid>

        {/* Role filter */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }} >
          <FormControl fullWidth size="small">
            <Select
              value={filters.role || ''}
              onChange={(e) => {
                const value = e.target.value;
                onRoleChange(value ? value : null);
              }}
              displayEmpty
              renderValue={(value) => {
                if (!value) return 'Wszystkie role';
                return value;
              }}
            >
              <MenuItem value="">Wszystkie role</MenuItem>
              <MenuItem value={UserRole.Admin}>{UserRole.Admin}</MenuItem>
              <MenuItem value={UserRole.Clerk}>{UserRole.Clerk}</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Status filters */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }} >
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.isActive === true}
                  onChange={(e) => {
                    onIsActiveChange(e.target.checked ? true : null);
                  }}
                  indeterminate={filters.isActive !== true && filters.isActive !== false}
                  size="small"
                />
              }
              label="Aktywny"
            />
          </Box>
        </Grid>

        {/* Email verification filter */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }} >
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.emailConfirmed === true}
                  onChange={(e) => {
                    onEmailConfirmedChange(e.target.checked ? true : null);
                  }}
                  indeterminate={
                    filters.emailConfirmed !== true && filters.emailConfirmed !== false
                  }
                  size="small"
                />
              }
              label="Email zweryfikowany"
            />
          </Box>
        </Grid>

        {/* Clear filters button */}
        {hasActiveFilters && (
          <Grid size={{ xs: 12 }} >
            <Button
              startIcon={<ClearIcon />}
              onClick={onClearFilters}
              variant="outlined"
              size="small"
            >
              Wyczyść wszystkie filtry
            </Button>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};
